import classNames from 'clsx';
import { flow } from 'es-toolkit';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { displayAds } from 'src/client/displayAds';
import { mintSingleton } from 'src/client/mint';
import {
  DISPLAY_ADS_LIBRARY_ENABLED,
  FREESTAR_ENABLED,
} from 'src/common/constants/experiments/ads';
import { withReactHooks } from 'src/common/decorators/withReactHooks';
import useExperiment from 'src/common/hooks/useExperiment';
import { selectExperiment } from 'src/common/selectors/config';
import {
  getDisplayAdAttributes,
  gptFreestarSlotsMap,
  gptSlotNames,
} from 'src/common/utils/ads/config';
import { useAdEligibilityConfig } from 'src/common/utils/ads/determineAdEligibility';
import DialogDisplayAd from './DialogDisplayAd';
import css from './now-playing-dialog-ad.module.scss';

export class NowPlayingDialogAd extends Component {
  static propTypes = {
    matchUrl: PropTypes.string.isRequired,
    guideId: PropTypes.string,
    loadedDisplaySlotId: PropTypes.string.isRequired,
    isFreestarEnabled: PropTypes.bool.isRequired,
    isDisplayAdsLibraryEnabled: PropTypes.bool.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = { isAdOpen: false, isAdDismissed: false };
    this.handleAdClose = this.handleAdClose.bind(this);
  }

  componentDidMount() {
    if (this.props.isDisplayAdsLibraryEnabled) {
      this.subscribeToDisplayAdsManager();
    }
  }

  componentDidUpdate(prevProps) {
    const { adEligibilityConfig, isDisplayAdsLibraryEnabled, loadedDisplaySlotId } = this.props;

    if (!prevProps.isDisplayAdsLibraryEnabled && isDisplayAdsLibraryEnabled) {
      this.subscribeToDisplayAdsManager();
    }

    if (prevProps.isDisplayAdsLibraryEnabled && !isDisplayAdsLibraryEnabled) {
      this.unsubscribeFromDisplayAdsManager();
    }

    if (
      !isDisplayAdsLibraryEnabled &&
      prevProps.loadedDisplaySlotId !== loadedDisplaySlotId &&
      loadedDisplaySlotId === gptFreestarSlotsMap[gptSlotNames.nowPlaying_side]
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ isAdOpen: true });
      return;
    }

    if (
      prevProps.adEligibilityConfig.isNowPlayingAdEligible &&
      !adEligibilityConfig.isNowPlayingAdEligible
    ) {
      this.handleAdClose();
    }
  }

  componentWillUnmount() {
    this.unsubscribeFromDisplayAdsManager();
  }

  subscribeToDisplayAdsManager() {
    const subscriptionId = (this.displayAdsSubscriptionId ?? 0) + 1;
    this.displayAdsSubscriptionId = subscriptionId;

    const subscribe = () => {
      if (this.displayAdsSubscriptionId !== subscriptionId) {
        return;
      }

      this.subscribeToDisplayAds();
    };

    if (displayAds.isReady) {
      subscribe();
      return;
    }

    displayAds.ready.then(subscribe);
  }

  unsubscribeFromDisplayAdsManager() {
    this.displayAdsSubscriptionId = (this.displayAdsSubscriptionId ?? 0) + 1;

    if (!this.isSubscribedToDisplayAds) {
      return;
    }

    displayAds.off('impression', this.handleDisplayAdLoaded);
    displayAds.off('companionLoaded', this.handleDisplayAdLoaded);
    this.isSubscribedToDisplayAds = false;
  }

  subscribeToDisplayAds = () => {
    if (this.isSubscribedToDisplayAds) {
      return;
    }

    displayAds.on('impression', this.handleDisplayAdLoaded);
    displayAds.on('companionLoaded', this.handleDisplayAdLoaded);
    this.isSubscribedToDisplayAds = true;
  };

  handleDisplayAdLoaded = (event) => {
    if (event.placement === gptSlotNames.nowPlaying_side && !this.state.isAdDismissed) {
      this.setState({ isAdOpen: true });
    }
  };

  handleAdClose() {
    if (this.props.isDisplayAdsLibraryEnabled) {
      this.setState({ isAdOpen: false, isAdDismissed: true });
      return;
    }

    mintSingleton.instance?.clearSlot(gptFreestarSlotsMap[gptSlotNames.nowPlaying_side], true);
    this.setState({ isAdOpen: false });
  }

  render() {
    const { matchUrl, guideId, isDisplayAdsLibraryEnabled, isFreestarEnabled } = this.props;

    const { isAdOpen, isAdDismissed } = this.state;

    return (
      <div
        data-testid="adOverlay"
        className={classNames({
          [css.adOverlay]: isAdOpen,
          [css.hiddenAdOverlay]: !isAdOpen,
        })}
      >
        <div className={css.adInnerContainer}>
          <div data-testid="closeIconContainer" className={css.adCloseIconContainer}>
            <i data-testid="adCloseIcon" className={css.adCloseIcon} onClick={this.handleAdClose} />
          </div>
          {(!isDisplayAdsLibraryEnabled || !isAdDismissed) && (
            <DialogDisplayAd
              isProfile={false}
              matchUrl={matchUrl}
              guideId={guideId}
              className={css.ad}
              {...getDisplayAdAttributes(gptSlotNames.nowPlaying_side, isFreestarEnabled)}
            />
          )}
        </div>
      </div>
    );
  }
}

export function mapStateToProps(state) {
  return {
    loadedDisplaySlotId: get(state, 'mint.loadedDisplaySlotId', ''),
    isFreestarEnabled: selectExperiment(state, FREESTAR_ENABLED),
  };
}

export default flow(
  connect(mapStateToProps),
  withReactHooks(() => ({
    adEligibilityConfig: useAdEligibilityConfig(),
    isDisplayAdsLibraryEnabled: useExperiment(DISPLAY_ADS_LIBRARY_ENABLED, false),
  })),
)(NowPlayingDialogAd);
